import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  FormGroup,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import { CheckCircle, Cancel, EmojiEvents } from '@mui/icons-material';
import { trainingService, QuizQuestion, QuizResult } from '../services/trainingService';

interface TrainingQuizProps {
  courseId: string;
  passingScore: number;
  onPass?: () => void;
  onCancel?: () => void;
}

export default function TrainingQuiz({
  courseId,
  passingScore,
  onPass,
  onCancel,
}: TrainingQuizProps) {
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<QuizResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const loadQuiz = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await trainingService.getQuiz(courseId);
      setQuestions(data.questions);
    } catch (err: any) {
      setError(err.message || 'Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: string, answer: string | string[]) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleSubmit = async () => {
    // Validate all questions answered
    const unanswered = questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      setError(`Please answer all questions (${unanswered.length} remaining)`);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const submission = {
        answers: Object.entries(answers).map(([questionId, answer]) => ({
          questionId,
          answer,
        })),
      };

      const quizResult = await trainingService.submitQuiz(courseId, submission);
      setResult(quizResult);

      if (quizResult.passed) {
        onPass?.();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setAnswers({});
    setResult(null);
    setError(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (questions.length === 0) {
    return (
      <Alert severity="info">
        No quiz available for this course. You can complete the course by finishing the video.
      </Alert>
    );
  }

  // Show results if quiz has been submitted
  if (result) {
    return (
      <Box>
        <Box textAlign="center" mb={4}>
          {result.passed ? (
            <>
              <EmojiEvents sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom color="success.main" fontWeight="bold">
                Congratulations! You Passed!
              </Typography>
              <Typography variant="h6" gutterBottom>
                Your Score: {result.score}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You answered {result.correctAnswers} out of {result.totalQuestions} questions correctly
              </Typography>
            </>
          ) : (
            <>
              <Cancel sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom color="error.main" fontWeight="bold">
                Quiz Not Passed
              </Typography>
              <Typography variant="h6" gutterBottom>
                Your Score: {result.score}%
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Passing score required: {passingScore}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You answered {result.correctAnswers} out of {result.totalQuestions} questions correctly
              </Typography>
            </>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Results Breakdown */}
        <Typography variant="h6" gutterBottom>
          Review Your Answers
        </Typography>
        {result.results.map((item, index) => (
          <Paper key={index} sx={{ p: 2, mb: 2 }}>
            <Box display="flex" alignItems="flex-start" gap={2}>
              {item.isCorrect ? (
                <CheckCircle color="success" />
              ) : (
                <Cancel color="error" />
              )}
              <Box flex={1}>
                <Typography variant="body1" fontWeight="bold" gutterBottom>
                  Question {index + 1}
                </Typography>
                {item.explanation && (
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {item.explanation}
                  </Typography>
                )}
                {!item.isCorrect && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Your answer:</strong> {formatAnswer(item.userAnswer)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Correct answer:</strong> {formatAnswer(item.correctAnswer)}
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Box>
          </Paper>
        ))}

        {/* Action Buttons */}
        <Box display="flex" gap={2} justifyContent="center" mt={4}>
          {!result.passed && (
            <Button variant="contained" onClick={handleRetry} size="large">
              Retry Quiz
            </Button>
          )}
          <Button variant="outlined" onClick={onCancel} size="large">
            {result.passed ? 'Continue' : 'Review Course'}
          </Button>
        </Box>
      </Box>
    );
  }

  // Show quiz questions
  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Course Assessment
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Answer all questions to complete the course. Passing score: {passingScore}%
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Questions */}
      {questions.map((question, index) => (
        <Paper key={question.id} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
            <Chip label={`Q${index + 1}`} color="primary" />
            <Box flex={1}>
              <Typography variant="body1" fontWeight="bold">
                {question.question}
              </Typography>
              {question.points > 1 && (
                <Typography variant="caption" color="text.secondary">
                  ({question.points} points)
                </Typography>
              )}
            </Box>
          </Box>

          {question.questionType === 'MULTIPLE_CHOICE' || question.questionType === 'TRUE_FALSE' ? (
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              >
                {(question.options as Array<{ id: string; text: string }>).map((option) => (
                  <FormControlLabel
                    key={option.id}
                    value={option.id}
                    control={<Radio />}
                    label={option.text}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          ) : (
            <FormControl component="fieldset" fullWidth>
              <FormGroup>
                {(question.options as Array<{ id: string; text: string }>).map((option) => (
                  <FormControlLabel
                    key={option.id}
                    control={
                      <Checkbox
                        checked={
                          Array.isArray(answers[question.id]) &&
                          (answers[question.id] as string[]).includes(option.id)
                        }
                        onChange={(e) => {
                          const current = (answers[question.id] as string[]) || [];
                          if (e.target.checked) {
                            handleAnswerChange(question.id, [...current, option.id]);
                          } else {
                            handleAnswerChange(
                              question.id,
                              current.filter((id) => id !== option.id)
                            );
                          }
                        }}
                      />
                    }
                    label={option.text}
                  />
                ))}
              </FormGroup>
            </FormControl>
          )}
        </Paper>
      ))}

      {/* Submit Button */}
      <Box display="flex" gap={2} justifyContent="flex-end">
        {onCancel && (
          <Button variant="outlined" onClick={onCancel} size="large">
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          size="large"
        >
          {submitting ? 'Submitting...' : 'Submit Quiz'}
        </Button>
      </Box>
    </Box>
  );
}

// Helper function to format answers for display
function formatAnswer(answer: string | string[]): string {
  if (Array.isArray(answer)) {
    return answer.join(', ');
  }
  return String(answer);
}

